import React, { useId } from 'react';

import { ComponentRenderer } from '@/components/dynamic-views/ComponentRenderer';
import { ManifestProvider } from '@/components/dynamic-views/ManifestContext';
import type { ViewComponent, ViewDefinition } from '@/types/dynamic_views';

export interface ManifestValueLabelProps {
  /** JSON string containing a ViewDefinition, or a pre-parsed ViewDefinition object */
  value: unknown;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  truncate?: boolean;
  onInteraction?: (interactionType: string, data: unknown) => void;
  isComposing?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

/**
 * Simple error boundary component for catching ComponentRenderer errors.
 * Defined inline to keep the component self-contained.
 */
class ManifestErrorBoundary extends React.Component<
  { children: React.ReactNode; onError: () => void },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; onError: () => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ManifestValueLabel render error:', error, errorInfo);
    this.props.onError();
  }

  render() {
    if (this.state.hasError) {
      return null; // Return null, parent will render fallback
    }
    return this.props.children;
  }
}

/**
 * Display component for manifest field type values.
 * Parses ViewDefinition JSON and renders components via ComponentRenderer.
 *
 * @example
 * <ManifestValueLabel value='{"version":"1.0","components":[...]}' />
 * // Renders: Component tree via ComponentRenderer
 */
export const ManifestValueLabel: React.FC<ManifestValueLabelProps> = ({
  value,
  fontSize = 'sm',
  className = '',
  truncate,
  onInteraction,
  isComposing,
}) => {
  const manifestId = useId();
  const sizeClass = fontSizeClasses[fontSize];
  const [renderError, setRenderError] = React.useState(false);

  // Reset error state when value changes
  React.useEffect(() => {
    setRenderError(false);
  }, [value]);

  // Handle null/undefined values
  if (value === null || value === undefined || value === '') {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  // Parse JSON string or use pre-parsed object
  let viewDefinition: ViewDefinition | null = null;

  if (typeof value === 'string') {
    try {
      viewDefinition = JSON.parse(value) as ViewDefinition;
    } catch (error) {
      console.warn('Failed to parse manifest JSON:', error, value);
      return <span className={`${sizeClass} ${className}`}>—</span>;
    }
  } else if (typeof value === 'object' && value !== null) {
    viewDefinition = value as ViewDefinition;
  }

  // Validate that parsed object has components array
  if (
    !viewDefinition ||
    !viewDefinition.components ||
    !Array.isArray(viewDefinition.components)
  ) {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  // If render error occurred, show fallback
  if (renderError) {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  const componentCount = viewDefinition.components.length;

  // Truncated view: show abbreviated text summary
  if (truncate) {
    const summary = `Manifest (${componentCount} component${componentCount !== 1 ? 's' : ''})`;
    return (
      <span
        className={`${sizeClass} ${className} block overflow-hidden text-ellipsis whitespace-nowrap`}
        title={summary}
      >
        {summary}
      </span>
    );
  }

  // Full view: render component tree
  // Generate stable key that reflects the full manifest value to ensure error boundary remounts on changes
  const stableKey =
    typeof value === 'string'
      ? value // Use full string to detect any content changes
      : JSON.stringify(
          viewDefinition.components.map(c => ({ id: c.id, props: c.props }))
        );

  return (
    <ManifestErrorBoundary key={stableKey} onError={() => setRenderError(true)}>
      <ManifestProvider value={manifestId}>
        <div className={`space-y-2 ${className}`} data-manifest-id={manifestId}>
          {viewDefinition.components.map(
            (component: ViewComponent, index: number) => (
              <ComponentRenderer
                key={component.id || `component-${index}`}
                component={component}
                onInteraction={onInteraction}
                isComposing={isComposing}
              />
            )
          )}
        </div>
      </ManifestProvider>
    </ManifestErrorBoundary>
  );
};
